import { useState } from 'react';
import { INMSurvey } from '@/types';
import { usePhrase, useRouter } from '@/hooks';
import {
    Alert,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Stack,
    Typography,
} from '@mui/material';
import { RESPONSE_ERROR_PHRASES } from '@/consts/phrases/errorPhrases';
import { NM_DASHBOARD_PHRASES } from '@/consts/phrases/nmDashboardPhrases';
import { Currency } from '@/components/general';
import { format } from 'date-fns';
import { config } from '@/config';
import { nmDashboardService } from '@/services';
import { Icon } from '@/components/general/Icon/Icon';

import styles from './NMSurveyCard.module.css';

interface NMSurveyCardProps {
    surveyData: INMSurvey;
}

export const NMSurveyCard = ({ surveyData }: NMSurveyCardProps) => {
    const { t } = usePhrase();
    const router = useRouter();

    const [isLoading, setIsLoading] = useState(false);
    const [gettingUrlError, setGettingUrlError] = useState(false);

    const isHonorariaHidden = surveyData.honoraria === 0 && !surveyData.honorariaPhraseId;
    const isLoiHidden = !surveyData.loi || surveyData.loi === 0;

    const hasUpToPhrase = surveyData.honorariaPhraseId === NM_DASHBOARD_PHRASES.UP_TO;

    const youCanEarnText = t(NM_DASHBOARD_PHRASES.YOU_CAN_EARN);
    const honorariaPhraseText = surveyData.honorariaPhraseId
        ? t(surveyData.honorariaPhraseId)
        : youCanEarnText;

    const onButtonClick = async () => {
        setIsLoading(true);
        try {
            const url = await nmDashboardService.getSurveyUrl(
                surveyData.surveyDisplayedId.toString(),
                surveyData.targetToken,
            );
            if (url) {
                router.push(url);
                setGettingUrlError(false);
            } else {
                setGettingUrlError(true);
            }
        } catch (err) {
            setGettingUrlError(true);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Card className={`${styles.card} nmSurveyCard`}>
            <CardHeader
                disableTypography
                className={`${styles.title} nmSurveyCardTitle`}
                title={surveyData.topic}
                sx={{ pb: 0 }}
            />
            <CardContent className={`${styles.baseInfo} nmSurveyCardBaseInfo`} sx={{ pt: 0 }}>
                <Stack
                    direction="row"
                    sx={{
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography variant="subtitle2">{t(surveyData.methodologyPhraseId)}</Typography>
                    <Typography variant="subtitle2">{`${t(NM_DASHBOARD_PHRASES.STUDY_NUMBER, {
                        asString: true,
                    })}: ${surveyData.projectNumber}`}</Typography>
                </Stack>
            </CardContent>
            <CardContent className="nmSurveyCardDetails">
                <Stack
                    direction="row"
                    className={styles.infoStack}
                    sx={{
                        justifyContent: 'flex-start',
                        flexWrap: 'wrap',
                    }}
                >
                    {!isHonorariaHidden && (
                        <Box className={`${styles.box} nmSurveyCardInfoBox`}>
                            <Box className={`${styles.icon} nmSurveyCardInfoIcon`}>
                                <Icon path="nmDashboardIcons/DashboardWalletIcon" />
                            </Box>
                            {surveyData.honoraria !== 0 && (
                                <Typography variant="h6" className="nmSurveyCardInfoTitle">
                                    {surveyData.honorariaPhraseId &&
                                        hasUpToPhrase &&
                                        `${t(surveyData.honorariaPhraseId, {
                                            asString: true,
                                        })} `}
                                    <Currency
                                        position={surveyData.currencySymbolPosition}
                                        symbol={surveyData.currencySymbol}
                                        code={surveyData.currencyCode ?? ''}
                                        amount={surveyData.honoraria}
                                    />
                                </Typography>
                            )}
                            <Typography variant="subtitle2" className="nmSurveyCardLabel">
                                {hasUpToPhrase ? youCanEarnText : honorariaPhraseText}
                            </Typography>
                        </Box>
                    )}
                    {!isLoiHidden && (
                        <Box className={`${styles.box} nmSurveyCardInfoBox`}>
                            <Box className={`${styles.icon} nmSurveyCardInfoIcon`}>
                                <Icon path="nmDashboardIcons/DashboardTimeIcon" />
                            </Box>
                            <Typography variant="h6" className="nmSurveyCardInfoTitle">
                                {surveyData.loi} {t(NM_DASHBOARD_PHRASES.MIN)}
                            </Typography>
                            <Typography variant="subtitle2" className="nmSurveyCardLabel">
                                {t(NM_DASHBOARD_PHRASES.SURVEY_LENGTH)}
                            </Typography>
                        </Box>
                    )}
                    <Box className={`${styles.box} nmSurveyCardInfoBox`}>
                        <Box className={`${styles.icon} nmSurveyCardInfoIcon`}>
                            <Icon path="nmDashboardIcons/DashboardDeadlineIcon" />
                        </Box>
                        <Typography variant="h6" className="nmSurveyCardInfoTitle">
                            {format(new Date(surveyData.completeDt), config.dateFormat)}
                        </Typography>
                        <Typography variant="subtitle2" className="nmSurveyCardLabel">
                            {t(NM_DASHBOARD_PHRASES.DEADLINE)}
                        </Typography>
                    </Box>
                </Stack>
            </CardContent>
            <CardActions
                className={styles.actionBar}
                sx={{
                    flexDirection: 'column',
                    alignItems: { xs: 'initial', md: 'flex-end' },
                    gap: 2,
                    pl: 2,
                    pr: 2,
                    pb: 2,
                }}
            >
                {gettingUrlError && (
                    <Alert severity="error" variant="outlined">
                        {t(RESPONSE_ERROR_PHRASES.SERVER_ERROR)}
                    </Alert>
                )}
                <Button
                    className={`nmSurveyCardButton ${styles.btn}`}
                    variant="contained"
                    sx={{
                        flexShrink: '0',
                        width: { xs: '100%', md: 'auto' },
                        whiteSpace: 'nowrap',
                    }}
                    onClick={onButtonClick}
                    loading={isLoading}
                >
                    {t(surveyData.surveyButtonPhraseId)}
                </Button>
            </CardActions>
        </Card>
    );
};
